/**
 * Which browser web work goes through: the user's own, or the Denker
 * browser - a browser profile of Denker's own, in the background. Rust owns
 * the choice - the tools it governs reach the companion over local HTTP,
 * never through the webview - so this module only reads and writes it. The
 * one choice covers every browser the extension runs in.
 */

import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";

import { isBrowserId, type BrowserId } from "./browser-connection";

export type BrowserLane = "yours" | "denker";

/** Where the Denker extension stands in one browser: "consent" is connected
 * before the person agreed on its welcome page, so Denker cannot work there
 * yet; "outdated" is connected but older than Denker works with, until the
 * browser updates it; "disabled" is in the profile but turned off by the
 * browser - a development build while Developer mode is off;
 * "turned_off", the person turned it off in the browser's extensions list;
 * "disconnected", the person disconnected it in Settings, so Denker uses it
 * nowhere until they connect it again. */
export type BrowserExtensionState =
  | "connected"
  | "consent"
  | "outdated"
  | "installed"
  | "disabled"
  | "turned_off"
  | "missing"
  | "disconnected";

/** The extension in one browser, and whether Connect offers its setup there. */
export type BrowserExtension = {
  state: BrowserExtensionState;
  setup: boolean;
};

export type BrowserExtensionStates = Partial<
  Record<BrowserId, BrowserExtension>
>;

type BrowserRow = {
  browser_id: string | null;
  /** Its data folder exists: only an installed browser has a row. */
  installed: boolean;
  extension: BrowserExtensionState;
  setup: boolean;
};

export const browserLaneApplies = (): boolean => isTauri();

export function getBrowserLane(): Promise<BrowserLane> {
  return invoke<BrowserLane>("browser_lane");
}

export function setBrowserLane(lane: BrowserLane): Promise<void> {
  return invoke("set_browser_lane", { lane });
}

/** The system's default browser, when it is one Denker knows. */
export async function getDefaultBrowser(): Promise<BrowserId | null> {
  const id = await invoke<unknown>("default_browser_id");
  return isBrowserId(id) ? id : null;
}

/** Bring the Denker browser up for the user, to sign in to sites. */
export function openDenkerBrowser(): Promise<void> {
  return invoke("open_denker_browser");
}

/** The Denker browser: the browser it runs - none without Chrome, Edge or
 * Brave - where the extension stands in it ("disconnected" never), and
 * whether it is set up: closed, it is then ready, not unconnected - agents
 * start it when they need it. */
export type DenkerBrowserStatus = {
  browserId: BrowserId | null;
  extension: BrowserExtension;
  ready: boolean;
};

export async function getDenkerBrowserStatus(): Promise<DenkerBrowserStatus> {
  const row = await invoke<Omit<BrowserRow, "installed"> & { ready: boolean }>(
    "denker_browser_status",
  );
  return {
    browserId: isBrowserId(row.browser_id) ? row.browser_id : null,
    extension: { state: row.extension, setup: row.setup === true },
    ready: row.ready === true,
  };
}

/** The Denker extension's state in each installed browser Settings has a row for. */
export async function listBrowserExtensions(): Promise<BrowserExtensionStates> {
  const rows = await invoke<BrowserRow[]>("denker_browser_browsers");
  const states: BrowserExtensionStates = {};
  for (const row of rows)
    if (row.installed && isBrowserId(row.browser_id))
      states[row.browser_id] = {
        state: row.extension,
        setup: row.setup === true,
      };
  return states;
}

/** Stop using the Denker extension in `browserId` until it is connected
 * again; the extension stays installed. */
export function disconnectBrowserExtension(
  browserId: BrowserId,
): Promise<void> {
  return invoke("disconnect_browser_extension", { browserId });
}

/** Use the Denker extension in `browserId` again, and check it: whether it
 * answered. */
export function connectBrowserExtension(
  browserId: BrowserId,
): Promise<boolean> {
  return invoke<boolean>("connect_browser_extension", { browserId });
}

/** Whether pages Denker opens get their cookie banner refused - or hidden
 * when it offers no refusal - and a pop-up over them closed. Off until the
 * person turns it on; Rust owns the choice, as it does the lane. */
export function getClosePopups(): Promise<boolean> {
  return invoke<boolean>("browser_close_popups");
}

export function setClosePopups(close: boolean): Promise<void> {
  return invoke("set_browser_close_popups", { close });
}

/** Rust tells every window when the choice changes, wherever it was made. */
export const CLOSE_POPUPS_CHANGED_EVENT =
  "denker://browser-close-popups-changed";

/** The hint that the choice is there was shown: Rust counts it and waits before the next. */
export function popupHintShown(): Promise<void> {
  return invoke("browser_popup_hint_shown");
}
